/**
 * Pages /grc/messages et /grc/messages/:id — Inbox complète + détail.
 *
 * - /grc/messages : liste paginée filtrable de TOUS les messages (signalements
 *   + grc_messages unifiés). Plus exhaustive que la preview du dashboard.
 * - /grc/messages/:id : détail d'un grc_message avec édition statut/assigné/réponse.
 *   Pour les signalements, on redirige vers /service-technique/signalements/:id (page L8-L10).
 *
 * Refonte R7.1 (mai 2026) :
 *   - Topbar glass cohérent avec halo grc (au lieu de bg-grc plein)
 *   - Sub-nav avec tabs en pills glass + bouton retour
 *   - Card filtres glass avec 6 selects + search Enter→reload
 *   - Liste items en cards glass interactives (au lieu de table dense)
 *   - Detail : layout 2 colonnes glass (contenu + sidebar actions)
 *   - Pagination en boutons R2
 *
 * Logique métier 100% préservée :
 *   - GrcInboxPage : api.grc.inbox() avec filtres source/canal/service/statut/assigneUserId/search
 *     · Pagination offset+limit (pageSize 30) · Search Enter→reload+page=0
 *   - GrcMessageDetailPage : api.grc.messages.get/update/delete · api.serviceTechnique.planning.getWeek
 *     pour récupérer agents assignables (manager+) · canEdit (super_admin/admin/manager) ·
 *     canDelete (super_admin/admin) · applyChanges détecte les changements et marque
 *     reponseEnvoyeeAt si statut='traite'
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';     // R13 — remplace window.confirm
import {
  Button, Badge, Card, Alert, Field, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { GrcLayout } from '@/components/grc/GrcLayout';
import {
  MessageCircle, ArrowLeft, Mail, FileText, Phone, FormInput,
  Building2, X, ChevronRight, Search, Inbox, Trash2,
  Send, CheckCircle2, AlertCircle, FileSpreadsheet, FileDown,
} from 'lucide-react';

const CANAUX_ICON: Record<string, typeof Mail> = {
  email:        Mail,
  courrier:     FileText,
  signalement:  MessageCircle,
  formulaire:   FormInput,
  message_asso: MessageCircle,
  telephone:    Phone,
  guichet:      Building2,
  autre:        FileText,
};

const CANAUX_LABEL: Record<string, string> = {
  email:        'Email',
  courrier:     'Courrier',
  signalement:  'Signalement',
  formulaire:   'Formulaire',
  message_asso: 'Message Asso',
  telephone:    'Téléphone',
  guichet:      'Guichet',
  autre:        'Autre',
};

const SERVICES_LABEL: Record<string, string> = {
  service_technique: 'Service technique',
  associations:      'Associations',
  scolaire:          'Scolaire',
  urbanisme:         'Urbanisme',
  grc:               'GRC',
  cantine:           'Cantine',
  etat_civil:        'État civil',
  autre:             'Autre',
};

const STATUTS_LABEL: Record<string, { label: string; tone: 'neutral' | 'warning' | 'success' | 'urgent' }> = {
  a_traiter: { label: 'À traiter', tone: 'warning' },
  en_cours:  { label: 'En cours',  tone: 'warning' },
  traite:    { label: 'Traité',    tone: 'success' },
  archive:   { label: 'Archivé',   tone: 'neutral' },
};

type InboxResult = Awaited<ReturnType<typeof api.grc.inbox>>;
type InboxItem = InboxResult['items'][number];
type GrcMessage = Awaited<ReturnType<typeof api.grc.messages.get>>;

// Tab pill — actif vs inactif
function GrcTab({
  children, active, onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  if (active) {
    return (
      <span className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-grc/30 bg-grc-tint/80 px-3 text-xs font-medium text-grc-fg backdrop-blur-glass">
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-transparent bg-transparent px-3 text-xs font-medium text-ink-muted backdrop-blur-glass transition-all hover:border-border hover:bg-surface/40 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
    >
      {children}
    </button>
  );
}

// =============================================================================
// PAGE LISTE COMPLÈTE
// =============================================================================

export function GrcInboxPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<InboxResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filterSource, setFilterSource] = useState<string>('');
  const [filterCanal, setFilterCanal] = useState<string>('');
  const [filterService, setFilterService] = useState<string>('');
  const [filterStatut, setFilterStatut] = useState<string>('');
  const [filterAssigne, setFilterAssigne] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 30;
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);

  async function doExport(kind: 'excel' | 'pdf') {
    setExporting(kind); setError(null);
    const filters = {
      statut: filterStatut || undefined,
      service: filterService || undefined,
      canal: filterCanal || undefined,
    };
    try {
      if (kind === 'excel') await api.grc.messages.exportExcel(filters);
      else await api.grc.messages.exportPdf(filters);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Export impossible');
    } finally { setExporting(null); }
  }

  async function reload() {
    setLoading(true); setError(null);
    try {
      const res = await api.grc.inbox({
        source: filterSource as 'all' | 'grc_message' | 'signalement' | undefined || undefined,
        canal: filterCanal || undefined,
        service: filterService || undefined,
        statut: filterStatut || undefined,
        assigneUserId: filterAssigne || undefined,
        search: search.trim() || undefined,
        limit: pageSize,
        offset: page * pageSize,
      });
      setData(res);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); /* eslint-disable-next-line */ },
    [filterSource, filterCanal, filterService, filterStatut, filterAssigne, page]);

  function navigateToItem(item: InboxItem) {
    if (item.source === 'signalement') {
      navigate(`/service-technique/signalements/${item.nativeId}`);
    } else {
      navigate(`/grc/messages/${item.nativeId}`);
    }
  }

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  // Styles selects partagés
  const selectCls = 'h-9 rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-xs text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

  return (
    <GrcLayout active="communication">
      {/* Sous-onglets de la section Communication */}
      <div className="border-b border-border px-4 py-2">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2">
          <GrcTab active>Boîte citoyenne</GrcTab>
          <GrcTab onClick={() => navigate('/grc/messagerie')}>Messagerie interne</GrcTab>
          <GrcTab onClick={() => navigate('/grc/alertes')}>Alertes municipales</GrcTab>
          <GrcTab onClick={() => navigate('/grc/newsletters')}>Newsletter</GrcTab>
        </div>
      </div>

      <main id="main-content" className="mx-auto max-w-[1400px] px-4 py-5 animate-fade-in">
        {/* En-tête de page */}
        <div className="mb-5 flex items-center gap-3">
          <LogoMark icon={Inbox} accent="grc" size="lg" />
          <div>
            <SectionEyebrow as="div">GRC & communication</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              Boîte citoyenne complète
            </h1>
            {data && (
              <p className="mt-0.5 text-sm text-ink-muted">
                {data.total} message{data.total > 1 ? 's' : ''} · page {page + 1} / {Math.max(1, totalPages)}
              </p>
            )}
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => void doExport('excel')} disabled={exporting !== null}>
              <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">{exporting === 'excel' ? 'Export…' : 'Excel'}</span>
            </Button>
            <Button variant="secondary" size="sm" onClick={() => void doExport('pdf')} disabled={exporting !== null}>
              <FileDown className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">{exporting === 'pdf' ? 'Export…' : 'PDF'}</span>
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="error" icon={AlertCircle} className="mb-3">
            {error}
          </Alert>
        )}

        {/* Filtres en Card glass */}
        <Card variant="glass" padded className="mb-3">
          <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            <select
              value={filterSource}
              onChange={(e) => { setFilterSource(e.target.value); setPage(0); }}
              className={selectCls}
              aria-label="Filtrer par source"
            >
              <option value="">Toutes sources</option>
              <option value="signalement">Signalements</option>
              <option value="grc_message">Messages GRC</option>
            </select>
            <select
              value={filterCanal}
              onChange={(e) => { setFilterCanal(e.target.value); setPage(0); }}
              className={selectCls}
              aria-label="Filtrer par canal"
            >
              <option value="">Tous canaux</option>
              <option value="signalement">Signalement</option>
              <option value="email">Email</option>
              <option value="courrier">Courrier</option>
              <option value="formulaire">Formulaire</option>
              <option value="telephone">Téléphone</option>
              <option value="guichet">Guichet</option>
              <option value="message_asso">Message Asso</option>
              <option value="autre">Autre</option>
            </select>
            <select
              value={filterService}
              onChange={(e) => { setFilterService(e.target.value); setPage(0); }}
              className={selectCls}
              aria-label="Filtrer par service"
            >
              <option value="">Tous services</option>
              {Object.entries(SERVICES_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select
              value={filterStatut}
              onChange={(e) => { setFilterStatut(e.target.value); setPage(0); }}
              className={selectCls}
              aria-label="Filtrer par statut"
            >
              <option value="">Tous statuts</option>
              <option value="open">À traiter / En cours</option>
              <option value="a_traiter">À traiter</option>
              <option value="en_cours">En cours</option>
              <option value="traite">Traité</option>
              <option value="archive">Archivé</option>
            </select>
            <select
              value={filterAssigne}
              onChange={(e) => { setFilterAssigne(e.target.value); setPage(0); }}
              className={selectCls}
              aria-label="Filtrer par assignation"
            >
              <option value="">Assignation : tous</option>
              <option value="me">Mes dossiers</option>
              <option value="unassigned">Non assigné</option>
            </select>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-dim" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPage(0); void reload(); } }}
                placeholder="Recherche…"
                className="h-9 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass pl-8 pr-2 text-xs text-ink placeholder:text-ink-dim shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                aria-label="Rechercher"
              />
            </div>
          </div>
        </Card>

        {/* Liste */}
        {loading ? (
          <Card variant="glass" padded>
            <div className="py-8 text-center">
              <div
                className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-grc border-t-transparent"
                role="status"
                aria-live="polite"
              />
              <p className="mt-4 text-sm text-ink-muted">Chargement des messages…</p>
            </div>
          </Card>
        ) : data && data.items.length === 0 ? (
          <Card variant="glass" padded>
            <div className="py-8 text-center">
              <div className="flex justify-center mb-3">
                <LogoMark icon={Inbox} accent="grc" size="lg" />
              </div>
              <p className="text-sm text-ink-muted">Aucun message ne correspond à ces filtres</p>
            </div>
          </Card>
        ) : data ? (
          <div className="space-y-2">
            {data.items.map((item) => {
              const CanalIcon = CANAUX_ICON[item.canal] ?? FileText;
              const statut = STATUTS_LABEL[item.statut] ?? STATUTS_LABEL.a_traiter!;
              const isUrgent = item.urgence === 'urgent';

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => navigateToItem(item)}
                  className={[
                    'group block w-full rounded-r-xl border backdrop-blur-glass px-4 py-3 text-left shadow-soft',
                    'transition-all duration-200 ease-out-expo',
                    'hover:-translate-y-0.5 hover:shadow-glass-hi focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                    isUrgent ? 'border-urgent/40' : 'border-border hover:border-grc/30',
                  ].join(' ')}
                  style={{
                    backgroundImage: isUrgent
                      ? 'radial-gradient(160px 80px at 0% 50%, oklch(var(--c-urgent) / 0.12), transparent 70%), linear-gradient(90deg, oklch(var(--c-surface) / 0.55), oklch(var(--c-bg-2) / 0.45))'
                      : 'linear-gradient(90deg, oklch(var(--c-surface) / 0.55), oklch(var(--c-bg-2) / 0.45))',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-grc/15 text-grc-fg flex-shrink-0"
                      aria-hidden="true"
                    >
                      <CanalIcon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={statut.tone} size="sm">{statut.label}</Badge>
                        <span className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                          {CANAUX_LABEL[item.canal]}
                        </span>
                        {item.numero && (
                          <span className="font-mono text-2xs text-ink-muted">{item.numero}</span>
                        )}
                        {isUrgent && <Badge variant="urgent" size="sm">Urgent</Badge>}
                      </div>
                      <p className="mt-1 truncate font-medium text-ink">{item.sujet}</p>
                      <p className="truncate text-xs text-ink-muted">
                        {item.isAnonymous ? (
                          <span className="italic">Anonyme</span>
                        ) : (
                          item.citoyenNom ?? item.citoyenEmail ?? '—'
                        )}
                        {item.service && (
                          <> · <span>{SERVICES_LABEL[item.service]}</span></>
                        )}
                      </p>
                    </div>
                    <div className="text-right text-2xs text-ink-subtle flex-shrink-0 hidden sm:block">
                      {new Date(item.receivedAt).toLocaleDateString('fr-FR')}
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-dim transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </div>
                </button>
              );
            })}
          </div>
        ) : null}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Précédent
            </Button>
            <span className="text-sm text-ink-muted tabular-nums">
              Page {page + 1} / {totalPages}
            </span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              Suivant
            </Button>
          </div>
        )}
      </main>
    </GrcLayout>
  );
}

// =============================================================================
// PAGE DÉTAIL MESSAGE GRC
// =============================================================================

export function GrcMessageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirm, dialogElement } = useConfirmDialog();   // R13
  const [message, setMessage] = useState<GrcMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editStatut, setEditStatut] = useState<string>('');
  const [editAssigne, setEditAssigne] = useState<string>('');
  const [agents, setAgents] = useState<Array<{ id: string; fullName: string }>>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const canEdit = ['super_admin', 'admin', 'manager'].includes(user?.role ?? '');
  const canDelete = ['super_admin', 'admin'].includes(user?.role ?? '');

  async function reload() {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      const data = await api.grc.messages.get(id);
      setMessage(data);
      setEditStatut(data.statut);
      setEditAssigne(data.assigneUserId ?? '');
      // Charge la liste des users assignables (depuis l'API planning qui a déjà les agents/managers)
      const week = await api.serviceTechnique.planning.getWeek(new Date().toISOString().slice(0, 10));
      setAgents(week.agents.map((a) => ({ id: a.id, fullName: a.fullName })));
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, [id]);

  async function applyChanges() {
    if (!message) return;
    setActionLoading(true); setError(null); setActionSuccess(null);
    try {
      const updates: Record<string, unknown> = {};
      if (editStatut !== message.statut) updates.statut = editStatut;
      if (editAssigne !== (message.assigneUserId ?? '')) updates.assigneUserId = editAssigne || null;
      if (Object.keys(updates).length === 0) {
        setActionSuccess('Aucun changement à enregistrer');
        return;
      }
      // Si on passe à traité, on marque la réponse envoyée
      if (updates.statut === 'traite' && !message.reponseEnvoyeeAt) {
        updates.reponseEnvoyeeAt = new Date().toISOString();
      }
      const updated = await api.grc.messages.update(message.id, updates);
      setMessage(updated);
      setActionSuccess('Modifications enregistrées');
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    } finally {
      setActionLoading(false);
    }
  }

  async function doDelete() {
    if (!message) return;
    // R13 — Dialog R2 au lieu de window.confirm
    const ok = await confirm({
      title: 'Supprimer ce message ?',
      description: 'Le message sera supprimé définitivement de la boîte de réception.',
      confirmLabel: 'Supprimer',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.grc.messages.delete(message.id);
      navigate('/grc/messages');
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur suppression');
    }
  }

  // L'en-tête + la barre d'onglets sont fournis par GrcLayout (cohérence inter-briques).

  if (loading) {
    return (
      <GrcLayout active="communication">
        <main className="mx-auto max-w-3xl px-4 py-12 text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-grc border-t-transparent" role="status" />
          <p className="mt-4 text-sm text-ink-muted">Chargement du message…</p>
        </main>
      </GrcLayout>
    );
  }

  if (error || !message) {
    return (
      <GrcLayout active="communication">
        <main className="mx-auto max-w-3xl px-4 py-8 animate-fade-in space-y-4">
          <Alert variant="error" icon={AlertCircle}>{error ?? 'Message introuvable'}</Alert>
          <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/grc/messages')}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Retour à l'inbox
          </Button>
        </main>
      </GrcLayout>
    );
  }

  const CanalIcon = CANAUX_ICON[message.canal] ?? FileText;
  const statut = STATUTS_LABEL[message.statut] ?? STATUTS_LABEL.a_traiter!;

  const inputCls = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

  return (
    <GrcLayout active="communication">
      {/* Sub-nav : retour inbox */}
      <div className="border-b border-border px-4 py-2">
        <div className="mx-auto max-w-[1400px]">
          <button
            type="button"
            onClick={() => navigate('/grc/messages')}
            className="inline-flex items-center gap-1.5 rounded-r-md px-1 text-xs text-ink-muted transition-colors hover:text-grc-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Inbox complète
          </button>
        </div>
      </div>

      <main id="main-content" className="mx-auto max-w-[1400px] px-4 py-5 animate-fade-in">
        {/* En-tête */}
        <div className="mb-4 flex items-center gap-3">
          <LogoMark icon={CanalIcon} accent="grc" size="lg" />
          <div className="min-w-0">
            <SectionEyebrow as="div">Message {(CANAUX_LABEL[message.canal] ?? '').toLowerCase()}</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-lg font-semibold tracking-r-snug text-ink truncate">
              {message.sujet}
            </h1>
          </div>
        </div>

        {actionSuccess && (
          <Alert variant="success" icon={CheckCircle2} className="mb-3">
            {actionSuccess}
          </Alert>
        )}
        {error && (
          <Alert variant="error" icon={AlertCircle} className="mb-3">
            {error}
          </Alert>
        )}

        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          {/* Colonne principale : contenu message */}
          <Card variant="glass" padded>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Badge variant={statut.tone} size="sm">{statut.label}</Badge>
              <Badge variant="grc" size="sm">{CANAUX_LABEL[message.canal]}</Badge>
              {message.service && (
                <Badge variant="neutral" size="sm">{SERVICES_LABEL[message.service]}</Badge>
              )}
              <span className="ml-auto text-2xs text-ink-muted">
                Reçu {new Date(message.receivedAt).toLocaleString('fr-FR')}
              </span>
            </div>

            {/* Citoyen */}
            <div>
              <SectionEyebrow as="h2">Citoyen</SectionEyebrow>
              <dl className="mt-2 grid gap-1.5 text-sm sm:grid-cols-2">
                {message.citoyenNom && (
                  <div className="flex items-baseline gap-2">
                    <dt className="text-ink-muted">Nom :</dt>
                    <dd className="text-ink font-medium">{message.citoyenNom}</dd>
                  </div>
                )}
                {message.citoyenEmail && (
                  <div className="flex items-baseline gap-2">
                    <dt className="text-ink-muted">Email :</dt>
                    <dd>
                      <a
                        href={`mailto:${message.citoyenEmail}`}
                        className="text-grc-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md"
                      >
                        {message.citoyenEmail}
                      </a>
                    </dd>
                  </div>
                )}
                {message.citoyenTelephone && (
                  <div className="flex items-baseline gap-2">
                    <dt className="text-ink-muted">Téléphone :</dt>
                    <dd>
                      <a
                        href={`tel:${message.citoyenTelephone}`}
                        className="text-grc-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md"
                      >
                        {message.citoyenTelephone}
                      </a>
                    </dd>
                  </div>
                )}
                {!message.citoyenNom && !message.citoyenEmail && !message.citoyenTelephone && (
                  <div className="text-sm italic text-ink-muted col-span-2">Anonyme</div>
                )}
              </dl>
            </div>

            {/* Contenu */}
            <div className="mt-5">
              <SectionEyebrow as="h2">Contenu</SectionEyebrow>
              {message.contenu ? (
                <div className="mt-2 rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass p-3">
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink">{message.contenu}</p>
                </div>
              ) : (
                <p className="mt-2 text-sm italic text-ink-muted">
                  Pas de contenu textuel (courrier scanné, message vocal…)
                </p>
              )}
            </div>

            {/* Pièces jointes */}
            {message.piecesJointes.length > 0 && (
              <div className="mt-5">
                <SectionEyebrow as="h2">Pièces jointes</SectionEyebrow>
                <ul className="mt-2 space-y-1">
                  {message.piecesJointes.map((pj, i) => (
                    <li key={i}>
                      <a
                        href={pj.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-r-md text-xs text-grc-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg px-1"
                      >
                        <FileText className="h-3 w-3" aria-hidden="true" />
                        {pj.filename}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Confirmation réponse */}
            {message.reponseEnvoyeeAt && (
              <Alert variant="success" icon={CheckCircle2} className="mt-4">
                Réponse envoyée le {new Date(message.reponseEnvoyeeAt).toLocaleString('fr-FR')}
              </Alert>
            )}
          </Card>

          {/* Sidebar : actions + métadonnées */}
          <aside aria-label="Actions" className="space-y-3">
            {canEdit ? (
              <Card variant="glass" padded>
                <SectionEyebrow as="h3" className="mb-3">Traitement</SectionEyebrow>

                <div className="space-y-3">
                  <Field label="Statut" htmlFor="msg-statut">
                    <select id="msg-statut" value={editStatut} onChange={(e) => setEditStatut(e.target.value)} className={inputCls}>
                      {Object.entries(STATUTS_LABEL).map(([v, s]) => (
                        <option key={v} value={v}>{s.label}</option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Assigné à" htmlFor="msg-assigne">
                    <select id="msg-assigne" value={editAssigne} onChange={(e) => setEditAssigne(e.target.value)} className={inputCls}>
                      <option value="">— Non assigné —</option>
                      {agents.map((a) => <option key={a.id} value={a.id}>{a.fullName}</option>)}
                    </select>
                  </Field>

                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    className="w-full"
                    disabled={actionLoading}
                    onClick={() => void applyChanges()}
                  >
                    <Send className="h-4 w-4" aria-hidden="true" />
                    {actionLoading ? 'Enregistrement…' : 'Enregistrer'}
                  </Button>

                  {message.citoyenEmail && (
                    <a
                      href={`mailto:${message.citoyenEmail}?subject=Re: ${encodeURIComponent(message.sujet)}`}
                      className="block"
                    >
                      <Button type="button" variant="secondary" size="sm" className="w-full" tabIndex={-1}>
                        <Mail className="h-4 w-4" aria-hidden="true" /> Répondre par email
                      </Button>
                    </a>
                  )}

                  {canDelete && (
                    <Button type="button" variant="danger" size="sm" className="w-full" onClick={() => void doDelete()}>
                      <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer
                    </Button>
                  )}
                </div>
              </Card>
            ) : (
              <Card variant="glass" padded>
                <p className="text-xs text-ink-muted">Lecture seule (rôle agent)</p>
              </Card>
            )}

            {/* Métadonnées */}
            <Card variant="glass" padded>
              <SectionEyebrow as="h3" className="mb-2">Métadonnées</SectionEyebrow>
              <dl className="space-y-1 text-2xs">
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-muted">Créé</dt>
                  <dd className="text-ink text-right">{new Date(message.createdAt).toLocaleString('fr-FR')}</dd>
                </div>
                {message.creeParFullName && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-ink-muted">Saisi par</dt>
                    <dd className="text-ink text-right truncate">{message.creeParFullName}</dd>
                  </div>
                )}
                <div className="flex justify-between gap-3">
                  <dt className="text-ink-muted">Modifié</dt>
                  <dd className="text-ink text-right">{new Date(message.updatedAt).toLocaleString('fr-FR')}</dd>
                </div>
                {message.categorieSujet && (
                  <div className="flex justify-between gap-3">
                    <dt className="text-ink-muted">Catégorie</dt>
                    <dd className="text-ink text-right">{message.categorieSujet}</dd>
                  </div>
                )}
              </dl>
            </Card>
          </aside>
        </div>
      </main>
      {dialogElement}{/* R13 — Dialog R2 (confirmation suppression message) */}
    </GrcLayout>
  );
}
